import api from './api'

const clientId = 'i-staging'

const responseHandler = (response) => {
  if (!response.ok) return Promise.reject(response)
  if (response.data.errors && response.data.errors.length > 0) return Promise.reject(response.data.errors)
  return response.data
}

export default class JoorService {
  constructor (domain, isJoorProduction = false) {
    this.domain = domain
    this.isJoorProduction = isJoorProduction
  }

  get tokenUrl () {
    // If is dev env, url would be: 'https://keycloak-istaging-demo.preview.joordev.com/auth/realms/joor/protocol/openid-connect/token'
    // If is prod env, url would be: 'https://auth.istaging-demo.preview.joordev.com/auth/realms/joor/protocol/openid-connect/token'
    return `https://${this.isJoorProduction ? 'auth.' : 'keycloak-'}${this.domain}/auth/realms/joor/protocol/openid-connect/token`
  }

  get apiUrl () {
    // If is dev env, url would be: 'https://udt-istaging-demo.preview.joordev.com'
    // If is prod env, url would be: 'https://udt.istaging-demo.preview.joordev.com'
    return `https://udt${this.isJoorProduction ? '.' : '-'}${this.domain}`
  }

  async getJoorToken ({ code, accountId, retailerId }) {
    const urlencoded = new URLSearchParams()
    urlencoded.append('grant_type', 'authorization_code')
    urlencoded.append('client_id', clientId)
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete('session_state')
    searchParams.delete('code')
    urlencoded.append('redirect_uri', `${window.location.origin + window.location.pathname}?${decodeURIComponent(searchParams.toString())}`)
    urlencoded.append('code', code)
    api.sendHeaders('Content-Type', 'application/x-www-form-urlencoded')
    const resp = await api.asyncRequest(this.tokenUrl, 'post', urlencoded)
    return responseHandler(resp)
  }

  async refreshJoorToken (refreshToken) {
    const urlencoded = new URLSearchParams()
    urlencoded.append('grant_type', 'refresh_token')
    urlencoded.append('client_id', clientId)
    urlencoded.append('refresh_token', refreshToken)
    api.sendHeaders('Content-Type', 'application/x-www-form-urlencoded')
    const resp = await api.asyncRequest(this.tokenUrl, 'post', urlencoded)
    return responseHandler(resp)
  }

  async getProducts ({ accountId, productIds, authToken }) {
    const productIdsQueryString = Array.isArray(productIds) ? productIds.join(',') : productIds
    const urlEnd = `${this.apiUrl}/products?account=${accountId}&product_ids=${productIdsQueryString}`
    api.sendHeaders('Authorization', `Bearer ${authToken}`)
    const resp = await api.asyncRequest(urlEnd, 'get')
    return responseHandler(resp)
  }

  async getSkus ({ accountId, productIds, authToken }) {
    const productIdsQueryString = Array.isArray(productIds) ? productIds.join(',') : productIds
    const urlEnd = `${this.apiUrl}/skus?account=${accountId}&product_ids=${productIdsQueryString}`
    api.sendHeaders('Authorization', `Bearer ${authToken}`)
    const resp = await api.asyncRequest(urlEnd, 'get')
    return responseHandler(resp)
  }

  async getPrices ({ accountId, skuIds, authToken }) {
    const skuIdsQueryString = Array.isArray(skuIds) ? skuIds.join(',') : skuIds
    const urlEnd = `${this.apiUrl}/prices?account=${accountId}&sku_ids=${skuIdsQueryString}`
    api.sendHeaders('Authorization', `Bearer ${authToken}`)
    const resp = await api.asyncRequest(urlEnd, 'get')
    return responseHandler(resp)
  }

  async getPriceType ({ accountId, authToken }) {
    const urlEnd = `${this.apiUrl}/price_types?account=${accountId}`
    api.sendHeaders('Authorization', `Bearer ${authToken}`)
    const resp = await api.asyncRequest(urlEnd, 'get')
    return responseHandler(resp)
  }

  async getOrders ({ accountId, authToken, retailerId }) {
    const urlEnd = `${this.apiUrl}/v4/orders?account=${retailerId}&status=IN_PROGRESS` // In this API, account id means retailerId
    api.sendHeaders('Authorization', `Bearer ${authToken}`)
    const resp = await api.asyncRequest(urlEnd, 'get')
    return responseHandler(resp)
  }

  async createOrder ({ accountId, authToken, retailerId, priceTypeId, collectionId }) {
    const urlEnd = `${this.apiUrl}/v4/orders/bulk_create?account=${accountId}&retailer=${retailerId}`
    api.sendHeaders('Authorization', `Bearer ${authToken}`)
    const resp = await api.asyncRequest(urlEnd, 'post', [{
      customer_id: retailerId,
      price_type_id: priceTypeId,
      collection_id: collectionId,
      status: 'IN_PROGRESS'
    }])
    return responseHandler(resp)
  }

  async addItemToOrder ({ accountId, authToken, retailerId, orderId, skuId, wholesaleValue }) {
    const urlEnd = `${this.apiUrl}/v4/orders/sku_line_items/bulk_create?account=${retailerId}` // In this API, account id means retailerId
    api.sendHeaders('Authorization', `Bearer ${authToken}`)
    const resp = await api.asyncRequest(urlEnd, 'post', [{
      order_id: orderId,
      sku_id: skuId,
      wholesale_price: wholesaleValue,
      quantity: 1
    }])
    return responseHandler(resp)
  }

  async getCollections ({ accountId, authToken }) {
    const urlEnd = `${this.apiUrl}/collections?account=${accountId}`
    api.sendHeaders('Authorization', `Bearer ${authToken}`)
    const resp = await api.asyncRequest(urlEnd, 'get')
    return responseHandler(resp)
  }
}
